"use client";

import {
  Stack,
  Heading,
  Box,
  Divider,
  Text,
  useToast,
  Radio,
  RadioGroup,
  Link,
  HStack,
  Collapse,
  Spinner,
} from "@chakra-ui/react";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { CustomInput } from "../components/Input";
import { CustomButton } from "../components/Buttons";
import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "../components/APIs";
import axios from "axios";
import { useApiData } from "../components/GetData";
import { WarningIcon } from "@chakra-ui/icons";
import FormNavigation from "../components/FormNavigation";
import FileUpload from "../components/FileUpload";
import Cookies from "js-cookie";
import { useFormik } from "formik";

export default function DBS() {
  const [isLoading, setLoading] = useState(false);
  const toast = useToast();
  const uid = Cookies.get("uid");

  const { data: DBSData, loading, fetchData } = useApiData("offer/getDBSData");
  const { data: offerAcceptance, fetchData: fetchOffer } = useApiData("onboarding/getOfferAccepted");

  const [pdfFile, setPdfFile] = useState(null);

  // 🔹 Initialize formik
  const formik = useFormik({
    initialValues: {
      required_type: "",
      dbs_type: "",
      dbs_number: "",
      dbs_file: "",
      onUpdatedService: "",
      user_id: uid,
    },
    onSubmit: async (data) => {
      console.table(data);
      try {
        if (!data.dbs_number || !data.dbs_file) {
          toast({
            title: "Incomplete Fields",
            description: "Please provide both the Certificate Number and upload your DBS file.",
            status: "warning",
            position: "top",
            isClosable: true,
          });
          return;
        }

        setLoading(true);
        const res = await axios.post(`${API_BASE_URL}offer/saveDBS`, data);
        const { status, message } = res.data;

        toast({
          title: status === "200" ? "Profile Updated" : "Update Error",
          description: message,
          status: status === "200" ? "success" : "error",
          position: "top",
          isClosable: true,
        });

        if (status === "200") fetchData(); // Refresh after successful save
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          status: "error",
          position: "top",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
  });

  // 🔹 Fetch data on mount
  useEffect(() => {
    fetchData();
    fetchOffer();
  }, []);

  // 🔹 Prefill data once fetched
  useEffect(() => {
    if (!DBSData) return;

    // Log missing data for easier debugging
    if (!DBSData.user_id) console.warn("Missing user_id in DBSData:", DBSData);

    // Set uploaded file preview
    setPdfFile(DBSData.dbs_file ? `${API_BASE_URL}uploads/${DBSData.dbs_file}` : null);

    // Pre-fill form values
    formik.setValues({
      required_type: DBSData.required_type || "",
      dbs_type: DBSData.dbs_type || "",
      dbs_number: DBSData.dbs_number || "",
      dbs_file: DBSData.dbs_file || "",
      onUpdatedService: DBSData.onUpdatedService || "",
      user_id: DBSData.user_id || uid,
    });
  }, [DBSData]);

  const requiredDBS = formik.values.required_type;
  const DBStype = formik.values.dbs_type;
  const onUpdatedService = formik.values.onUpdatedService;

  // 🔹 Disable button logic (auto)
  const disableSubmit =
    (DBStype === "none" && requiredDBS === "yes") ||
    (requiredDBS === "yes" &&
      (!onUpdatedService || !formik.values.dbs_number || !formik.values.dbs_file));

  if (loading) return <Spinner mt={10} />;

  return (
    <OnboardingLayout
      pageTitle="DBS"
      mainContent={
        <Stack as="form" onSubmit={formik.handleSubmit} gap={6}>
          {offerAcceptance?.accept === 1 ? (
            <>
              <Heading fontSize="lg" textTransform="uppercase">
                DBS Check
              </Heading>
              <Divider />

              {/* DBS Requirement */}
              <Text fontWeight={600}>Is a DBS check required for your role?</Text>
              <RadioGroup
                name="required_type"
                value={requiredDBS}
                onChange={(value) => formik.setFieldValue("required_type", value)}
              >
                <Stack spacing={3}>
                  <Radio value="yes" colorScheme="blue">
                    Yes, my job requires a DBS check
                  </Radio>
                  <Radio value="no" colorScheme="red">
                    No, my job does not require a DBS check
                  </Radio>
                </Stack>
              </RadioGroup>

              {/* If DBS Required */}
              <Collapse in={requiredDBS === "yes"}>
                <Box mt={4} p={4} rounded="lg" bg="gray.50" border="1px" borderColor="gray.200">
                  {/* DBS Type */}
                  <Text fontWeight={600} mb={2}>
                    Type of DBS check held
                  </Text>
                  <RadioGroup
                    name="dbs_type"
                    value={formik.values.dbs_type}
                    onChange={(value) => {
                      formik.setFieldValue("dbs_type", value);
                      // Reset fields when user switches option
                     
                    }}
                  >
                    <Stack spacing={3}>
                      <Radio value="Enhanced" colorScheme="orange">
                        Enhanced DBS
                      </Radio>
                      <Radio value="Basic" colorScheme="orange">
                        Basic DBS
                      </Radio>
                      <Radio value="none" colorScheme="orange">
                        I don't have a DBS yet
                      </Radio>
                    </Stack>
                  </RadioGroup>

                  {/* If User Has DBS */}
                  <Text fontWeight={600} mb={2}>
                    Is it registered on the Update Service?
                  </Text>

                  <RadioGroup
                    name="onUpdatedService"
                    value={formik.values.onUpdatedService}
                    onChange={(value) => {
                      formik.setFieldValue("onUpdatedService", value);
                      // Reset fields when user switches option
                      if (value === "") {
                        formik.setFieldValue("dbs_number", "");
                        formik.setFieldValue("dbs_file", "");
                      }
                    }}
                  >
                    <HStack spacing={5}>
                      <Radio value="yes" colorScheme="orange">
                        Yes
                      </Radio>
                      <Radio value="no" colorScheme="orange">
                        No
                      </Radio>
                    </HStack>
                  </RadioGroup>

                  {/* Show required fields only after Yes/No selection */}
                  <Collapse in={onUpdatedService === "yes" || onUpdatedService === "no"}>
                    <Stack py={5} spacing={5}>
                      <CustomInput
                        value={formik.values.dbs_number}
                        formLabel="Certificate Number"
                        name="dbs_number"
                        onChange={formik.handleChange}
                        type="text"
                        isRequired
                      />

                      <FileUpload
                        onFileUpload={(file) => formik.setFieldValue("dbs_file", file)}
                        required
                        existingFileUrl={pdfFile}
                      />

                      {pdfFile && (
                        <Text fontSize="sm" color="blue.500">
                          <Link href={pdfFile} target="_blank">
                            View Previously Uploaded File
                          </Link>
                        </Text>
                      )}
                    </Stack>
                  </Collapse>


                  {/* If user doesn’t have DBS */}
                  <Collapse in={DBStype === "none"}>
                    <Box mt={4}>
                      <Text>
                        You can apply for a new DBS{" "}
                        <Link
                          color="blue.500"
                          target="_blank"
                          href="https://www.hr-platform.co.uk/individual/application-login/?HT6u7Zv9CHXDkPSlVKq6CYHMniEhelow0fiOCbpNteab%2Fr%2B3adgiv9zHZUCM5WZkzzXGLbFV6m6x%2F6Zd%2FDGMWINCe8YSxht7oBmC%2F%2B6G1AlQQ4xI7ExcOQRk4MwyaZ64"
                        >
                          here
                        </Link>
                        .
                      </Text>
                    </Box>
                  </Collapse>
                </Box>
              </Collapse>

              {/* Action Buttons */}
              <Stack direction="row" justify="space-between" mt={4}>
                <FormNavigation
                  formik={formik}
                  isLoading={isLoading}
                  setIsLoading={setLoading}
                  nextRoute="/application/mobility-status"
                  previousRoute="/employment-check/referee"
                  progressKey="dbs"
                  isDisabled={disableSubmit}
                />
              </Stack>
            </>
          ) : (
            <Stack
              width={["100%", "60%"]}
              mx="auto"
              bg="red.50"
              rounded="lg"
              alignItems="center"
              p="2em"
              textAlign="center"
            >
              <WarningIcon fontSize="3em" color="red.500" />
              <Text color="red.600" fontSize="15pt" fontWeight={700}>
                Please accept the provisional offer{" "}
                <Link color="blue" href="/employment-check/provisional-offer">
                  here
                </Link>{" "}
                before submitting your Proof of Right to Work.
              </Text>
            </Stack>
          )}
        </Stack>
      }
    />
  );
}
